import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, LocateFixed } from "lucide-react";
import { placesAutocomplete, reverseGeocode } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Reusable Google Places autocomplete input (via backend proxy).
// `bare` renders a borderless/transparent input for use inside a grouped surface.
// `showGps` renders a trailing "use my location" button.
export default function PlacesAutocomplete({ value, onChange, placeholder, icon: Icon = MapPin, bare = false, showGps = false, outline = false, iconClass = "text-[#F5B400]" }) {
  const { lang } = useLang();
  const [text, setText] = useState(value?.description || "");
  const [preds, setPreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setText(value?.description || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleInput = (v) => {
    setText(v);
    onChange(null);
    if (timer.current) clearTimeout(timer.current);
    if (v.length < 2) { setPreds([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await placesAutocomplete(v, undefined, lang);
        setPreds(res);
        setOpen(true);
      } catch (e) { setPreds([]); }
      finally { setLoading(false); }
    }, 280);
  };

  const select = (p) => {
    setText(p.description);
    onChange({ description: p.description, place_id: p.place_id });
    setOpen(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await reverseGeocode(latitude, longitude, lang);
          if (res?.address) {
            setText(res.address);
            onChange({ description: res.address, place_id: res.place_id });
            setOpen(false);
          }
        } finally { setGpsLoading(false); }
      },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const inputCls = outline
    ? "w-full h-16 rounded-2xl bg-white border border-neutral-200 ps-12 pe-12 text-[16px] font-medium text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 transition-colors"
    : bare
    ? "w-full h-14 bg-transparent border-0 ps-11 pe-11 text-[15px] font-medium text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
    : "w-full h-14 rounded-xl bg-neutral-50 border border-neutral-100 ps-11 pe-11 text-[15px] font-medium text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-200 focus:ring-2 focus:ring-[#F5B400]/40 transition-colors";

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <span className={`absolute ${outline ? "start-4" : "start-3.5"} top-1/2 -translate-y-1/2 flex items-center pointer-events-none`}>
          <Icon className={`w-[18px] h-[18px] ${iconClass}`} />
        </span>
        <input
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => preds.length && setOpen(true)}
          placeholder={placeholder}
          className={inputCls}
        />
        {loading ? (
          <Loader2 className="absolute end-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
        ) : showGps ? (
          <button
            type="button"
            onClick={useMyLocation}
            aria-label="Use my location"
            title="Use my location"
            className="absolute end-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-neutral-400 hover:text-[#0a0a0a] hover:bg-neutral-100 transition-colors"
          >
            {gpsLoading ? <Loader2 className="w-4 h-4 animate-spin text-[#F5B400]" /> : <LocateFixed className="w-[18px] h-[18px]" />}
          </button>
        ) : null}
      </div>
      {open && preds.length > 0 && (
        <ul className="absolute z-50 mt-2 w-full bg-white border border-neutral-100 rounded-2xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.18)] max-h-64 overflow-auto py-1.5">
          {preds.map((p, pi) => (
            <li key={p.place_id}>
              <button
                type="button"
                data-testid={`places-suggestion-${pi}`}
                onClick={() => select(p)}
                className="w-full text-start px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 flex items-start gap-2.5 transition-colors"
              >
                <MapPin className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                <span>{p.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
